import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Col,
  Form,
  Row,
  Spinner,
  Stack,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import {
  faClose,
  faPenToSquare,
  faRefresh,
  faTrash,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash';

import useLocalStore from 'src/store/useLocalStore';

import ColumnConfiguration from 'src/components/custom_components/ColumnConfiguration';
import CustomInputGroupWithIcon from 'src/components/custom_components/customInputGroupWithIcon';
import CustomTable, {
  Column,
} from 'src/components/custom_components/CustomTable/CustomTable';

import { Api } from 'src/models/Api.model';
import { getColumnKey } from 'src/models/ColumnConfiguration.model';
import { CustomUserProfileFieldEnum } from 'src/models/GalenData/CustomField.model';
import { CustomFieldData } from 'src/models/GalenData/CustomFieldData.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import { getLocaleCode, Language } from 'src/models/Language.model';

import useGetCurrentUser from 'src/hooks/useGetCurrentUser';
import useGetUsers from 'src/hooks/useGetUsers';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';

import formatUserRole from 'src/utils/formatUserRole';

import AddUserFormModal from './AddUserFormModal';
import EditUserFormModal from './EditUserFormModal';

const DEFAULT_PAGE_SIZE = 10;

const SubUserTab = () => {
  const { isGettingUsers, handleGetUsers, users } = useGetUsers();

  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<GalenDataUser | null>(null);
  const [loadingUserId, setLoadingUserId] = useState<string | null>(null);
  const [userSettingsData, setUserSettingsData] =
    useState<CustomFieldData<CustomUserProfileFieldEnum>[]>();

  const [currentPage, setCurrentPage] = useState<number>(1);

  const { tCommon, tPages, language } = useUiTranslation();

  const { userEmail } = useGetCurrentUser();

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const handleShowAddUserModal = () => {
    setCurrentUser(null);
    setShowAddUserModal(true);
  };

  const handleCloseAddUserModal = () => {
    setShowAddUserModal(false);
  };

  const fetchUserSettings = useCallback(
    async (userId: string) => {
      try {
        const { data } = await Api.remote.getUserSettings(userId);
        return data;
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: errorMessage,
          locale: getLocaleCode(language as Language),
          type: 'error',
        });
      }
    },
    [handleHttpRequestError, tCommon, language],
  );

  const handleShowEditUserModal = useCallback(
    async (user: GalenDataUser) => {
      try {
        setLoadingUserId(user.userId);
        setCurrentUser(user);
        const data = await fetchUserSettings(user.userId);
        setUserSettingsData(data);
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: errorMessage,
          locale: getLocaleCode(language as Language),
          type: 'error',
        });
      } finally {
        setLoadingUserId(null);
      }
    },
    [fetchUserSettings, handleHttpRequestError, tCommon, language],
  );

  const handleCloseEditUserModal = () => {
    setUserSettingsData(undefined);
  };

  const {
    register,
    setValue,
    getValues,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      emailAddress: '',
      name: '',
    },
  });

  const initializeUsersList = useCallback(async () => {
    await handleGetUsers(DEFAULT_PAGE_SIZE);
    setCurrentPage(1);
    reset();
  }, [handleGetUsers, reset]);

  useEffect(() => {
    initializeUsersList();
  }, [initializeUsersList]);

  const handleDeleteUser = useCallback(
    (user: GalenDataUser) => {
      Api.confirmBox({
        title: tCommon('bootbox.title.Confirm'),
        message: tPages('users.deleteUserMessage', {
          emailAddress: user.emailAddress,
          fullName: user.fullName,
        }),
        locale: getLocaleCode(language as Language),
        callback: async (yes) => {
          if (!yes) {
            return;
          }

          try {
            await Api.remote.delete_user(user.userId);

            initializeUsersList();

            Api.alertBox({
              title: tCommon('bootbox.title.Success'),
              message: tPages('users.deleteUserSuccessMessage', {
                emailAddress: user.emailAddress,
                fullName: user.fullName,
              }),
              locale: getLocaleCode(language as Language),
              type: 'success',
            });
          } catch (error) {
            const errorMessage = handleHttpRequestError(error);
            Api.alertBox({
              title: tCommon('bootbox.title.Error'),
              message: errorMessage,
              locale: getLocaleCode(language as Language),
              type: 'error',
            });
          }
        },
      });
    },
    [initializeUsersList, handleHttpRequestError, language, tCommon, tPages],
  );

  const columns: Column<GalenDataUser>[] = useMemo(
    () => [
      { title: tPages('users.loginUsername'), key: 'emailAddress' },
      { title: tPages('users.fullName'), key: 'fullName' },
      {
        title: tPages('users.userType'),
        dataIndex: 'userType',
        render: (record) => (
          <div>
            {record.roles.map((role, index) => (
              <ul key={`${role.role}${index}`}>
                <li>
                  {role.role &&
                    tCommon(
                      `patientFields.userRoleEnum.${formatUserRole(role.role)}`,
                    )}{' '}
                  {role.practice?.name && `(${role.practice.name})`}
                </li>
              </ul>
            ))}
          </div>
        ),
      },
      {
        title: tPages('users.organization'),
        dataIndex: 'organization',
        render: (record) => <div>{record.currentRole?.practice?.name}</div>,
      },
      {
        title: tPages('users.actions'),
        dataIndex: 'actions',
        render: (record) => {
          return (
            <ButtonGroup className="gap-2">
              <Button
                variant="outline-primary"
                className="rounded-3 px-3 py-1"
                data-test="edit-member-button"
                onClick={() => {
                  handleShowEditUserModal(record);
                }}
                disabled={loadingUserId === record.userId}
              >
                {loadingUserId === record.userId ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <FontAwesomeIcon icon={faPenToSquare} className="me-0" />
                )}
              </Button>
              <Button
                variant="outline-danger"
                className="rounded-3 px-3 py-1"
                data-test="delete-member-button"
                onClick={() => {
                  handleDeleteUser(record);
                }}
                hidden={record.emailAddress === userEmail}
              >
                <FontAwesomeIcon icon={faTrash} className="me-0" />
              </Button>
            </ButtonGroup>
          );
        },
      },
    ],
    [
      handleDeleteUser,
      handleShowEditUserModal,
      tCommon,
      tPages,
      loadingUserId,
      userEmail,
    ],
  );

  const { usersColsKeys, setUsersColsKeys } = useLocalStore();

  const defaultVisibleColumns = useMemo(() => {
    const defaultColumnsConfiguration = Object.fromEntries(
      columns.map((column) => [getColumnKey(column), true]),
    );

    return usersColsKeys ?? defaultColumnsConfiguration;
  }, [columns, usersColsKeys]);

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    defaultVisibleColumns,
  );

  const handleToggleColumn = (key: string) => {
    setVisibleColumns((prev) => {
      const updatedVisibleColumns = {
        ...prev,
        [key]: !prev[key],
      };

      setUsersColsKeys(updatedVisibleColumns);

      return updatedVisibleColumns;
    });
  };

  const displayedColumns = useMemo(
    () => columns.filter((column) => visibleColumns[getColumnKey(column)]),
    [columns, visibleColumns],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce(() => {
        handleGetUsers(DEFAULT_PAGE_SIZE, 0, {
          emailAddress: getValues('emailAddress'),
          name: getValues('name'),
        });
        setCurrentPage(1);
      }, 1000),
    [handleGetUsers, getValues],
  );

  return (
    <Stack gap={3} className="h-100">
      <Row>
        <Col xl={4}>
          <h3>{tPages('users.title')}</h3>
          <div className="text-primary d-flex gap-2 align-items-center">
            <FontAwesomeIcon icon={faPenToSquare} />
            {tPages('users.edit')}
            <span>|</span>
            <FontAwesomeIcon icon={faTrash} />
            {tPages('users.delete')}
          </div>
        </Col>
        <Col
          xl={8}
          className="d-xl-flex justify-content-end align-items-center"
        >
          <Button
            variant="primary"
            className="d-inline-block ms-3 py-3"
            data-test="add-member-button"
            onClick={handleShowAddUserModal}
          >
            <FontAwesomeIcon
              icon={faUserPlus}
              className="me-2"
            ></FontAwesomeIcon>
            {tPages('users.add')}
          </Button>

          <Button
            variant="outline-primary"
            className="d-inline-block ms-3 py-3"
            onClick={initializeUsersList}
          >
            <FontAwesomeIcon
              icon={faRefresh}
              className="me-2"
            ></FontAwesomeIcon>
            {tPages('users.reload')}
          </Button>
        </Col>
      </Row>

      <Row>
        <Stack direction="horizontal" gap={5} className="align-items-end">
          <Row className="flex-fill">
            <Col md={5}>
              <Form.Group as={Col} controlId="emailAddress">
                <Form.Label>{tPages('users.loginUsername')}</Form.Label>
                <CustomInputGroupWithIcon>
                  <Form.Control
                    autoComplete="off"
                    type="text"
                    placeholder={tPages('users.loginUsername')}
                    {...register('emailAddress', {
                      onChange: (event) => {
                        setValue('emailAddress', event.target.value);
                        debouncedSearch();
                      },
                    })}
                  />
                </CustomInputGroupWithIcon>
              </Form.Group>
            </Col>

            <Col md={5}>
              <Form.Group as={Col} controlId="name">
                <Form.Label>{tPages('users.fullName')}</Form.Label>
                <CustomInputGroupWithIcon>
                  <Form.Control
                    autoComplete="off"
                    type="text"
                    placeholder={tPages('users.fullName')}
                    {...register('name', {
                      onChange: (event) => {
                        setValue('name', event.target.value);
                        debouncedSearch();
                      },
                    })}
                  />
                </CustomInputGroupWithIcon>
              </Form.Group>
            </Col>

            <Col md={2} className="d-flex align-items-end">
              <Button
                variant="outline-warning"
                onClick={initializeUsersList}
                disabled={!isDirty}
                data-testid="reset-button"
                className="py-2"
              >
                <FontAwesomeIcon icon={faClose} />
              </Button>
            </Col>
          </Row>
          <ColumnConfiguration
            columns={columns}
            visibleColumns={visibleColumns}
            onToggleColumn={handleToggleColumn}
          />
        </Stack>
      </Row>

      <Row className="flex-grow-1" style={{ minHeight: 0 }}>
        <Col className="d-flex flex-column h-100">
          <div className="flex-grow-1" style={{ minHeight: 0 }}>
            <CustomTable
              data-test="members-list"
              isLoading={isGettingUsers}
              dataSource={users.content ?? []}
              rowKey="userId"
              columns={displayedColumns}
              rowProps={(): React.HTMLAttributes<HTMLTableRowElement> =>
                ({
                  'data-test': 'member-item',
                }) as React.HTMLAttributes<HTMLTableRowElement>
              }
              pagination={{
                defaultPageSize: DEFAULT_PAGE_SIZE,
                pageSizeOptions: [DEFAULT_PAGE_SIZE],
                totalPages: users.totalPages ?? 1,
                currentPage,
                handleSetCurrentPage: (page) => {
                  setCurrentPage(page);
                },
                onChangePage: (pageNumber, pageSize) => {
                  handleGetUsers(pageSize, pageNumber - 1, {
                    emailAddress: getValues('emailAddress'),
                    name: getValues('name'),
                  });
                },
              }}
              empty={{
                buttonText: tPages('users.addNewUser'),
                onButtonClick: handleShowAddUserModal,
              }}
            />
          </div>
        </Col>
      </Row>

      {currentUser && userSettingsData && (
        <EditUserFormModal
          handleGetUsers={handleGetUsers}
          show={!!setUserSettingsData}
          handleClose={handleCloseEditUserModal}
          user={currentUser}
          pageSize={DEFAULT_PAGE_SIZE}
          currentPage={currentPage}
          searchValues={{
            emailAddress: getValues('emailAddress'),
            name: getValues('name'),
          }}
          userSettingsData={userSettingsData}
        />
      )}
      <AddUserFormModal
        initializeUsersList={initializeUsersList}
        show={showAddUserModal}
        handleClose={handleCloseAddUserModal}
      />
    </Stack>
  );
};

export default SubUserTab;
